/**
 * Persistent store for chronicle unlock conditions.
 * Some chronicles are locked by default and only become available
 * after the player discovers specific endings in other chronicles.
 */

const STORAGE_KEY = "chronicles-unlocked";

export interface UnlockRule {
  /** Chronicle that gets unlocked */
  chronicleId: string;
  /** The flag (from another chronicle's playthrough) that triggers the unlock */
  requiredFlag: string;
  /** Source chronicle where the flag is earned */
  sourceChronicleId: string;
  /** Message shown when the unlock happens */
  unlockMessage: string;
  /** Hint shown on the locked chronicle */
  lockedHint: string;
}

/**
 * All unlock rules. Add new entries here to lock chronicles behind endings.
 */
export const UNLOCK_RULES: UnlockRule[] = [
  {
    chronicleId: "dutch-golden-age-voc",
    requiredFlag: "ending_escape_north",
    sourceChronicleId: "eighty-years-war",
    unlockMessage: "Your journey north has opened a new chapter: the Dutch Golden Age awaits.",
    lockedHint: "A life rebuilt in the north may open the next age. Some fates in the war carry echoes beyond the final chapter.",
  },
  {
    chronicleId: "dutch-golden-age-voc",
    requiredFlag: "ending_merchant_north",
    sourceChronicleId: "eighty-years-war",
    unlockMessage: "Your merchant success in Amsterdam has opened a new chapter: the Dutch Golden Age awaits.",
    lockedHint: "A life rebuilt in the north may open the next age. Some fates in the war carry echoes beyond the final chapter.",
  },
  {
    chronicleId: "dutch-golden-age-voc",
    requiredFlag: "ending_quiet_north",
    sourceChronicleId: "eighty-years-war",
    unlockMessage: "Your quiet life in the north has opened a new chapter: the Dutch Golden Age awaits.",
    lockedHint: "A life rebuilt in the north may open the next age. Some fates in the war carry echoes beyond the final chapter.",
  },
  {
    chronicleId: "first-voyages-west",
    requiredFlag: "ending_morisco",
    sourceChronicleId: "fall-of-granada",
    unlockMessage: "Granada has fallen. Spain turns its gaze westward: the First Voyages await.",
    lockedHint: "Complete the Fall of Granada to unlock this chronicle. The end of Al-Andalus opens the age of exploration.",
  },
  {
    chronicleId: "first-voyages-west",
    requiredFlag: "ending_exile",
    sourceChronicleId: "fall-of-granada",
    unlockMessage: "Granada has fallen. Spain turns its gaze westward: the First Voyages await.",
    lockedHint: "Complete the Fall of Granada to unlock this chronicle. The end of Al-Andalus opens the age of exploration.",
  },
  {
    chronicleId: "first-voyages-west",
    requiredFlag: "ending_integration",
    sourceChronicleId: "fall-of-granada",
    unlockMessage: "Granada has fallen. Spain turns its gaze westward: the First Voyages await.",
    lockedHint: "Complete the Fall of Granada to unlock this chronicle. The end of Al-Andalus opens the age of exploration.",
  },
  {
    chronicleId: "first-voyages-west",
    requiredFlag: "ending_resistance",
    sourceChronicleId: "fall-of-granada",
    unlockMessage: "Granada has fallen. Spain turns its gaze westward: the First Voyages await.",
    lockedHint: "Complete the Fall of Granada to unlock this chronicle. The end of Al-Andalus opens the age of exploration.",
  },
  {
    chronicleId: "first-voyages-west",
    requiredFlag: "ending_siege_death",
    sourceChronicleId: "fall-of-granada",
    unlockMessage: "Granada has fallen. Spain turns its gaze westward: the First Voyages await.",
    lockedHint: "Complete the Fall of Granada to unlock this chronicle. The end of Al-Andalus opens the age of exploration.",
  },
  // The House of Medici → The Italian Wars. The banker's son, Matteo di
  // Bernardo, takes up the sword where his father's road ends — in Bologna,
  // as the wars begin. Only the three "carried the family to Bologna" endings
  // open his story (the Eighty Years' War → Dutch Golden Age pattern), so the
  // son's Bologna start is always grounded in how the father's chronicle ended.
  {
    chronicleId: "italian-wars",
    requiredFlag: "ending_med_bologna_banker",
    sourceChronicleId: "medici-florence",
    unlockMessage: "Bernardo's bank is reborn in Bologna, and his son comes of age in the condottieri's country: the Italian Wars await.",
    lockedHint: "A banker's son takes up the sword where his father's road ends. See the Medici house carried safe out of Florence's fall (north, to begin again in a new city) and his story opens.",
  },
  {
    chronicleId: "italian-wars",
    requiredFlag: "ending_med_bologna_bentivoglio",
    sourceChronicleId: "medici-florence",
    unlockMessage: "Bernardo serves the lords of Bologna, and his son comes of age in the condottieri's country: the Italian Wars await.",
    lockedHint: "A banker's son takes up the sword where his father's road ends. See the Medici house carried safe out of Florence's fall (north, to begin again in a new city) and his story opens.",
  },
  {
    chronicleId: "italian-wars",
    requiredFlag: "ending_med_bologna_quiet",
    sourceChronicleId: "medici-florence",
    unlockMessage: "Bernardo sets the family down in Bologna, and his son comes of age in the condottieri's country: the Italian Wars await.",
    lockedHint: "A banker's son takes up the sword where his father's road ends. See the Medici house carried safe out of Florence's fall (north, to begin again in a new city) and his story opens.",
  },
];

// ── In-memory cache (2026-05) ─────────────────────────────────────
// Same pattern as chronicleMemoryStore: getStore() is called once per
// chronicle on the home page (every RegionMarker reads
// `isChronicleUnlocked(chronicleId)`, and the home filters per
// chronicle as well). Without caching that's ~30+ fresh
// localStorage.getItem + JSON.parse calls per home render. Cache once,
// invalidate on write + cross-tab storage events.
let cache: string[] | null = null;

function getStore(): string[] {
  if (cache !== null) return cache;
  try {
    cache = JSON.parse(typeof localStorage !== "undefined" ? (localStorage.getItem(STORAGE_KEY) || "[]") : "[]");
  } catch {
    cache = [];
  }
  return cache!;
}

function saveStore(ids: string[]) {
  cache = ids;
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY || e.key === null /* clear() */) {
      cache = null;
    }
  });
}

/** Test-only: drop the cache so the next read re-parses from
 *  localStorage. Same rationale as the memory store's reset helper —
 *  same-tab `localStorage.clear()` doesn't fire a storage event. */
export function _resetUnlockCacheForTests() {
  cache = null;
}

// ── PLAYTEST OVERRIDE (temporary) ─────────────────────────────────
// Chronicles listed here are treated as ALWAYS unlocked, regardless of
// their UNLOCK_RULES, so they can be playtested directly from the home
// map and the Archivist ledger. The real lock (and its hint) stays
// defined above — to restore one, just remove the id from this set.
// (Empty: Italian Wars now uses its real lock — it opens only from the
// Medici "Bologna" endings — so it is no longer force-unlocked here.)
const PLAYTEST_FORCE_UNLOCKED = new Set<string>([]);

/** Check if a chronicle has been unlocked by the player */
export function isChronicleUnlocked(chronicleId: string): boolean {
  if (PLAYTEST_FORCE_UNLOCKED.has(chronicleId)) return true;
  return getStore().includes(chronicleId);
}

/** Persistently mark a chronicle as unlocked */
export function unlockChronicle(chronicleId: string): void {
  const store = getStore();
  if (!store.includes(chronicleId)) {
    store.push(chronicleId);
    saveStore(store);
  }
}

/** Get the unlock rule for a given chronicle, or undefined if it has no lock */
export function getUnlockRule(chronicleId: string): UnlockRule | undefined {
  return UNLOCK_RULES.find((r) => r.chronicleId === chronicleId);
}

/**
 * Given a set of flags from a completed playthrough, check if any
 * new chronicles should be unlocked. Returns the list of newly unlocked rules.
 * @param completedChronicleId - the chronicle that was just completed; only
 *   rules whose sourceChronicleId matches are evaluated.
 */
export function checkAndUnlock(flags: Set<string>, completedChronicleId?: string): UnlockRule[] {
  const newlyUnlocked: UnlockRule[] = [];
  for (const rule of UNLOCK_RULES) {
    if (completedChronicleId && rule.sourceChronicleId !== completedChronicleId) continue;
    if (flags.has(rule.requiredFlag) && !isChronicleUnlocked(rule.chronicleId)) {
      unlockChronicle(rule.chronicleId);
      newlyUnlocked.push(rule);
    }
  }
  return newlyUnlocked;
}
